export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export function parseLogLevelFromEnv(
  value: string | undefined,
): LogLevel | undefined {
  if (!value) {
    return undefined;
  }
  switch (value.trim().toLowerCase()) {
    case 'debug':
      return LogLevel.DEBUG;
    case 'info':
      return LogLevel.INFO;
    case 'warn':
      return LogLevel.WARN;
    case 'error':
      return LogLevel.ERROR;
    default:
      return undefined;
  }
}

export function logLevelMeetsMinimum({
  entryLevel,
  minimumLevel,
}: {
  entryLevel: LogLevel;
  minimumLevel: LogLevel;
}): boolean {
  return entryLevel >= minimumLevel;
}

export type LogEntry = {
  timestamp: string;
  level: LogLevel;
  component: string;
  message: string;
  context?: Record<string, unknown>;
  error?: Error;
};

export type SerializedLogEntry = {
  timestamp: string;
  level: number;
  levelName: string;
  component: string;
  message: string;
  context?: Record<string, unknown>;
  errorMessage?: string;
  errorStack?: string;
};

export function serializeLogEntry(entry: LogEntry): SerializedLogEntry {
  return {
    timestamp: entry.timestamp,
    level: entry.level,
    levelName: LogLevel[entry.level],
    component: entry.component,
    message: entry.message,
    context: entry.context,
    errorMessage: entry.error?.message,
    errorStack: entry.error?.stack,
  };
}

export class Logger {
  private static listeners: Array<(entry: LogEntry) => void> = [];

  constructor(private component: string) {}

  static addListener(listener: (entry: LogEntry) => void): void {
    Logger.listeners.push(listener);
  }

  static removeListener(listener: (entry: LogEntry) => void): void {
    const index = Logger.listeners.indexOf(listener);
    if (index > -1) {
      Logger.listeners.splice(index, 1);
    }
  }

  private log(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
    error?: Error,
  ): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      component: this.component,
      message,
      context,
      error,
    };

    this.logToConsole(entry);

    Logger.listeners.forEach((listener) => {
      try {
        listener(entry);
      } catch (err) {
        console.error('Logger listener error:', err);
      }
    });
  }

  private logToConsole(entry: LogEntry): void {
    const levelName = LogLevel[entry.level];
    const timestamp = entry.timestamp.slice(11, 23); // HH:MM:SS.mmm
    const prefix = `[${timestamp}] ${levelName} ${entry.component}:`;

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(prefix, entry.message, entry.context || '');
        break;
      case LogLevel.INFO:
        console.info(prefix, entry.message, entry.context || '');
        break;
      case LogLevel.WARN:
        console.warn(prefix, entry.message, entry.context || '');
        break;
      case LogLevel.ERROR:
        console.error(
          prefix,
          entry.message,
          entry.context || '',
          entry.error || '',
        );
        break;
    }
  }

  debug(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.DEBUG, message, context);
  }

  info(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.INFO, message, context);
  }

  warn(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.WARN, message, context);
  }

  error(
    message: string,
    context?: Record<string, unknown>,
    error?: Error,
  ): void {
    this.log(LogLevel.ERROR, message, context, error);
  }
}
